import { PeopleSdk } from 'lib-klab-sdks';

type PagedEmployeeConfigurationResponse = PeopleSdk.PagedEmployeeConfigurationResponse;
type EmployeeConfigurationResponse = PeopleSdk.EmployeeConfigurationResponse;
type EmployeeConfigurationByIdResponse = PeopleSdk.EmployeeConfigurationByIdResponse;
type EmployeeConfigurationInternalResponse = PeopleSdk.EmployeeConfigurationInternalResponse;

function buildPage(number: number, size: number, totalElements: number): PeopleSdk.PaginationResponse {
    return {
        number,
        size,
        totalElements,
        totalPages: Math.max(1, Math.ceil(totalElements / size)),
    };
}

function deepClone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}

const loc = (id: number, name: string): PeopleSdk.LocationObject => ({ locationId: id, name });
const dept = (id: number, name: string): PeopleSdk.DepartmentObject => ({ departmentId: id, name });
const role = (id: number, name: string): PeopleSdk.RoleObject => ({ roleId: id, name });
const emp = (id: number, code: string, name: string, lastname: string): PeopleSdk.EmployeeObject => ({
    employeeId: id,
    code,
    name,
    lastname,
});

function buildConfigItem(
    configId: number,
    employeeId: number,
    code: string,
    name: string,
    lastname: string,
    locationId: number,
    locationName: string,
    deptId: number,
    deptName: string,
    roleId: number,
    roleName: string,
    workModality: PeopleSdk.WorkModalityEnum,
    type: 'DEFAULT' | 'CUSTOM',
): EmployeeConfigurationResponse {
    return {
        employeeConfigurationId: configId,
        employee: emp(employeeId, code, name, lastname),
        location: loc(locationId, locationName),
        department: dept(deptId, deptName),
        role: role(roleId, roleName),
        type,
        workModality,
        inactivityThresholdSeconds: 300,
        screenshotIntervalSeconds: 600,
        allowMultiDevice: false,
        maxDevicesAllowed: 1,
        configHash: `hash-${configId}`,
        createdAt: '2024-01-01T00:00:00',
        updatedAt: '2024-01-01T00:00:00',
    };
}

const defaultConfigs: EmployeeConfigurationResponse[] = [
    buildConfigItem(1, 1, 'EMP001', 'Juan', 'Pérez', 1, 'Lima HQ', 1, 'RRHH', 1, 'Líder', PeopleSdk.WorkModalityEnumValues.Office, 'DEFAULT'),
    buildConfigItem(2, 2, 'EMP002', 'María', 'García', 1, 'Lima HQ', 1, 'RRHH', 2, 'Colaborador', PeopleSdk.WorkModalityEnumValues.Hybrid, 'CUSTOM'),
    buildConfigItem(3, 3, 'EMP003', 'Carlos', 'López', 2, 'Arequipa', 2, 'Operaciones', 1, 'Líder', PeopleSdk.WorkModalityEnumValues.Remote, 'DEFAULT'),
];

class EmployeeConfigurationsSdkMockStore {
    private configs: EmployeeConfigurationResponse[] = deepClone(defaultConfigs);

    getAllEmployeeConfigurations(
        page = 0,
        size = 10,
        _locationId?: number,
        _workModality?: PeopleSdk.WorkModalityEnum,
        _roleId?: number,
    ): PagedEmployeeConfigurationResponse {
        const p = Math.max(0, page);
        const s = Math.max(1, size);
        const total = this.configs.length;
        const start = p * s;
        const content = this.configs.slice(start, start + s);
        return {
            content: deepClone(content),
            page: buildPage(p, s, total),
        };
    }

    getEmployeeConfigurationById(employeeConfigurationId: number): EmployeeConfigurationByIdResponse {
        const config = this.configs.find(c => c.employeeConfigurationId === employeeConfigurationId);
        if (!config) throw { status: 404, message: 'Configuración no encontrada' };
        return {
            ...deepClone(config),
            functions: [],
        } as EmployeeConfigurationByIdResponse;
    }

    updateEmployeeConfiguration(
        _employeeConfigurationId: number,
        _payload: PeopleSdk.EmployeeConfigurationUpdateRequest,
    ): void {

    }

    getEmployeeConfiguration(
        employeeId?: number,
        _employeeEmail?: string,
    ): EmployeeConfigurationInternalResponse {
        const config = employeeId != null
            ? this.configs.find(c => c.employee.employeeId === employeeId)
            : this.configs[0];
        if (!config) throw { status: 404, message: 'Configuración no encontrada' };
        return {
            employee: {
                employeeId: config.employee.employeeId,
                code: config.employee.code,
                name: config.employee.name,
                lastname: config.employee.lastname,
            } as any,
            workModality:
                config.workModality as PeopleSdk.WorkModalityEnum,
            type: config.type,
            inactivityThresholdSeconds: config.inactivityThresholdSeconds,
            multiDevice: { allowMultiDevice: config.allowMultiDevice, maxDevicesAllowed: config.maxDevicesAllowed } as any,
            functions: {} as any,
        };
    }
}

const store = new EmployeeConfigurationsSdkMockStore();

export const employeeConfigurationsSdkMock = {
    getAllEmployeeConfigurations: (
        page?: number,
        size?: number,
        locationId?: number,
        workModality?: PeopleSdk.WorkModalityEnum,
        roleId?: number,
    ) => store.getAllEmployeeConfigurations(page ?? 0, size ?? 10, locationId, workModality, roleId),
    getEmployeeConfigurationById: (employeeConfigurationId: number) =>
        store.getEmployeeConfigurationById(employeeConfigurationId),
    updateEmployeeConfiguration: (
        employeeConfigurationId: number,
        payload: PeopleSdk.EmployeeConfigurationUpdateRequest,
    ) => store.updateEmployeeConfiguration(employeeConfigurationId, payload),
    getEmployeeConfiguration: (employeeId?: number, employeeEmail?: string) =>
        store.getEmployeeConfiguration(employeeId, employeeEmail),
};
